import * as React from 'react';
import { BoltIcon } from '@patternfly/react-icons';
import { isAdmin, logout } from '@app/utils/utils';
import { PURPLE_GRADIENT, colors, radii, shadows } from '@app/components/designTokens';

/**
 * Route guard for the admin console. A token alone is not enough — the session
 * must carry ROLE_ADMIN. Non-admins (e.g. a stale token, or a role downgrade)
 * never reach the app shell. This is the UX gate; the backend's
 * @PreAuthorize("hasRole('ADMIN')") is the actual security boundary.
 */
export const RequireAdmin: React.FunctionComponent<{ children: React.ReactNode }> = ({ children }) => {
  if (isAdmin()) {
    return <>{children}</>;
  }

  const styles: { [k: string]: React.CSSProperties } = {
    page: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)',
      padding: 24
    },
    card: {
      width: '100%',
      maxWidth: 440,
      background: '#ffffff',
      borderRadius: radii.card,
      boxShadow: shadows.softCard,
      padding: 40,
      textAlign: 'center'
    },
    logoIcon: {
      width: 48,
      height: 48,
      borderRadius: 12,
      background: PURPLE_GRADIENT,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: colors.boltGold,
      fontSize: 24,
      marginBottom: 16
    },
    title: { fontSize: 20, fontWeight: 700, color: colors.title, marginBottom: 8 },
    body: { color: colors.subtle, fontSize: 14, marginBottom: 24, lineHeight: 1.5 },
    button: {
      padding: '12px 22px',
      fontSize: 14,
      fontWeight: 600,
      color: '#ffffff',
      background: PURPLE_GRADIENT,
      border: 'none',
      borderRadius: radii.button,
      cursor: 'pointer'
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logoIcon}>
          <BoltIcon />
        </div>
        <div style={styles.title}>Access restricted</div>
        <p style={styles.body}>
          This console is limited to AdVolt platform operators with the <code>ROLE_ADMIN</code>{' '}
          role. Your account doesn&apos;t have access.
        </p>
        <button style={styles.button} onClick={() => logout()}>
          Sign out
        </button>
      </div>
    </div>
  );
};
